import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import React, { useCallback, useRef, useState } from 'react';

import { BeforeImage, Divider, Handle, Label, SliderRoot } from './Styles';

type Props = {
  beforeSrc: string;
  afterSrc: string;
  beforeLabel: string;
  afterLabel: string;
  className?: string;
};

const BeforeAfterSlider = ({
  beforeSrc,
  afterSrc,
  beforeLabel,
  afterLabel,
  className,
}: Props) => {
  const [position, setPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);

  const updateFromClientX = useCallback((clientX: number) => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const { left, width } = container.getBoundingClientRect();

    if (!width) {
      return;
    }

    const next = ((clientX - left) / width) * 100;
    setPosition(Math.min(100, Math.max(0, next)));
  }, []);

  return (
    <SliderRoot
      ref={containerRef}
      className={className}
      role="slider"
      tabIndex={0}
      aria-label={`${beforeLabel} / ${afterLabel}`}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(position)}
      onPointerDown={(event) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        updateFromClientX(event.clientX);
      }}
      onPointerMove={(event) => {
        if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
          return;
        }

        updateFromClientX(event.clientX);
      }}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          setPosition((current) => Math.max(0, current - 5));
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          setPosition((current) => Math.min(100, current + 5));
        }
      }}
    >
      <img src={afterSrc} alt={afterLabel} draggable={false} />
      <BeforeImage src={beforeSrc} alt={beforeLabel} draggable={false} $position={position} />
      <Divider $position={position}>
        <Handle>
          <LeftOutlined />
          <RightOutlined />
        </Handle>
      </Divider>
      <Label $side="before">{beforeLabel}</Label>
      <Label $side="after">{afterLabel}</Label>
    </SliderRoot>
  );
};

export default BeforeAfterSlider;
